import {useDates} from '~/composables/useDates';
import {useDeviceLocale} from '~/composables/useDeviceLocale';

/**
 * `useDates` bound to the DEVICE locale, for display call sites.
 *
 * `useDates` itself is pure — locale in, string out — which is what makes it
 * testable. Threading the locale through every table cell and chip that shows
 * a date would bury that plumbing in 20+ files, so this binder does the one
 * ambient read in one place and the call sites stay one-argument:
 *
 *   {{ shortDate(itemRow.createdAt) }}       01/Nov/27
 *   {{ shortDateTime(link.startsAt) }}       01/Nov/27 15:30 · 01/Nov/27 3:30 PM (US)
 *   {{ longDate(m.currentPeriodEnd) }}        17 November 2026 (inside a sentence)
 *
 * The compact display date is day/Mon/yy for EVERY reader — a named month
 * cannot be transposed — so only the clock follows the device. The typed
 * INPUT fields are the only place a digit-order date survives, and they say
 * their order in the label (dd/mm/yyyy · mm/dd/yyyy).
 *
 * The locale is the DEVICE's (see useDeviceLocale — Accept-Language on the
 * server, the same value hydrated to the client), matching the Flutter app.
 * The browser fallback serves outside a Nuxt context.
 */
export function useLocalisedDates() {
  const {formatDate, formatShortDate, formatShortDateTime, formatLongDate, formatWeekdayDate, currentLocale} = useDates();

  let deviceLocale = null;
  try {
    deviceLocale = useDeviceLocale();
  } catch {
    // Not in a component's setup — the browser fallback below serves.
  }
  const tag = () => deviceLocale?.value || currentLocale();

  // weekdayDate (X101): Thu 22 Oct 2026, the house short date with its weekday (the member detail dialog).
  // date (Grant 2026-11-02): "2 Nov 2027", the plain date for a term, a period
  // or a "Leaves" chip on the membership pages (the app writes the same); the
  // compact shortDate stays for table cells.
  return {
    date: (value) => formatDate(value, tag()),
    shortDate: (value) => formatShortDate(value),
    shortDateTime: (value) => formatShortDateTime(value, tag()),
    longDate: (value) => formatLongDate(value, tag()),
    weekdayDate: (value) => formatWeekdayDate(value, tag()),
  };
}
